/*
 * Shared cart-mutation lock.
 *
 * Two auto-add engines write to the same cart: free samples (B2C) and B2B free
 * units. A doctor can legitimately hold both kinds of product at once — for
 * example by adding B2C items before logging in — so the two are serialized
 * against EACH OTHER through a single promise chain, instead of each engine
 * guarding itself with a local flag.
 *
 * SCOPE — this is not a store-wide cart mutex. Other cart writers in this theme
 * do not take the lock: the `line-item:change` addon handler and the cart page's
 * own updateCart() (both inline in layout/theme.liquid), and theme.js's own
 * cart/change.js calls. Serializing only the two auto-add engines is enough
 * because both are idempotent and re-converge on the next cart:change — an
 * interleaved third-party write just gets reconciled on the following pass.
 * Do not assume a mutation elsewhere in the theme is protected by this.
 *
 * Queue, never drop: a sync arriving mid-flight still runs afterwards, rather
 * than being silently skipped, or the cart is left reflecting a state that has
 * since changed. A rejecting engine can never wedge the queue for the other —
 * the chain is re-seeded with a caught derivative of each link, so the next
 * caller's `fn` always runs regardless of how the previous one settled.
 *
 * install(host) attaches host.withCartLock / host.UniverskinCartQueue.
 * Idempotent: a host that already has a queue keeps it, so re-running install
 * (e.g. if the script tag were ever injected twice) never resets in-flight work.
 *
 * Loaded in the browser, this installs directly onto window: window.withCartLock
 * and window.UniverskinCartQueue. Under Node, require() returns the bare
 * `install` function so tests can build a lock over an isolated fake host
 * instead of mutating a real global (module-level shared state would make
 * tests order-dependent on each other).
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    factory()(root);
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function install(host) {
    host.UniverskinCartQueue = host.UniverskinCartQueue || Promise.resolve();
    host.withCartLock = function (fn) {
      var run = function () { return fn(); };
      // Run on both fulfilment and rejection so one engine failing cannot
      // deadlock the other.
      var next = host.UniverskinCartQueue.then(run, run);
      host.UniverskinCartQueue = next.catch(function () {});
      return next;
    };
    return host;
  }

  return install;
});
